import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_PRODUCT_ALERTS } from "@lib/shared_constants";

import { productAlertTargetLimitReachedErrorCode } from "./constants";

export type ProductAlertStatus = "active" | "disabled";

/** Per-Canvas alerts target "canvas"; workspace alerts target "app_group". */
export type ProductAlertTarget = "canvas" | "app_group";

/** The automated workspace alert types, one record of each per app group. */
export type WorkspaceAlertType = "CANVAS_ENTRY_VOLUME_CHANGE" | "CANVAS_SEND_VOLUME_CHANGE" | "CANVAS_ARCHIVED";

export interface ProductAlertsParams {
  query?: {
    name?: string;
    status?: ProductAlertStatus;
    canvas_ids?: string[];
    /** "app_group" lists the workspace alerts; omitted lists per-Canvas alerts. */
    target?: ProductAlertTarget;
  };
  start?: number;
  limit?: number;
  sortby?: string;
  sortdir?: number;
}

export const productAlertThresholdUnitVolume = "volume" as const;
export const productAlertThresholdUnitPercentage = "percentage" as const;

export type ProductAlertThresholdUnit =
  | typeof productAlertThresholdUnitVolume
  | typeof productAlertThresholdUnitPercentage;

export interface ProductAlertCondition {
  metric: string;
  subject: string;
  operator: string;
  threshold_value: number;
  threshold_unit: ProductAlertThresholdUnit;
}

export interface ProductAlertConditionGroup {
  condition_operator: string;
  conditions: ProductAlertCondition[];
}

export interface ProductAlertRules {
  /** null on the CANVAS_ARCHIVED workspace alert, which has no metric rules. */
  group_operator: string | null;
  condition_groups: ProductAlertConditionGroup[];
}

/** Dashboard user groups an alert's email can go to alongside its typed addresses. */
export type ProductAlertRecipientGroup = "all_admins" | "object_editors";

export interface ProductAlertEmailNotificationSettings {
  recipients: string[];
  /** Only workspace alerts accept recipient groups; omitted when the alert names none. */
  recipient_groups?: ProductAlertRecipientGroup[];
}

export interface ProductAlertNotificationSettings {
  email?: ProductAlertEmailNotificationSettings;
  slack?: { url: string };
  webhook?: { url: string; headers?: Record<string, string> };
}

export interface ProductAlertSchedule {
  schedule_type: string;
  /** null on the CANVAS_ARCHIVED workspace alert, which is event-driven rather than periodic. */
  schedule_time_seconds: number | null;
  lookback_seconds: number | null;
  schedule_anchor_seconds?: number;
}

export interface UpdateProductAlertParams {
  name: string;
  target: string;
  target_id: string;
  status: ProductAlertStatus;
  rules: ProductAlertRules;
  schedule: ProductAlertSchedule;
  notification_settings: ProductAlertNotificationSettings;
}

export type CreateProductAlertParams = UpdateProductAlertParams;

export interface PostProductAlertProps extends UpdateProductAlertParams {}

export interface PutProductAlertProps extends UpdateProductAlertParams {
  id: string;
}

/** CANVAS_ARCHIVED is saved with its schedule_type only. */
export interface PutWorkspaceProductAlertSchedule extends Partial<ProductAlertSchedule> {
  schedule_type: string;
}

/** Full update of a workspace alert; admins only. */
export interface PutWorkspaceProductAlertProps {
  id: string;
  name: string;
  target: "app_group";
  target_id: string;
  alert_type: WorkspaceAlertType;
  status: ProductAlertStatus;
  /** Omitted for CANVAS_ARCHIVED, which has no metric rules. */
  rules?: ProductAlertRules;
  schedule: PutWorkspaceProductAlertSchedule;
  notification_settings: ProductAlertNotificationSettings;
}

export interface ProductAlertListResultItem {
  id: string;
  target: string;
  target_id: string;
  /** null for workspace alerts, which target the app group rather than a Canvas. */
  target_name: string | null;
  /** Set on workspace alerts; null on per-Canvas alerts. */
  alert_type?: WorkspaceAlertType | null;
  name: string;
  status: ProductAlertStatus;
  rules: ProductAlertRules;
  schedule: ProductAlertSchedule;
  notification_settings?: ProductAlertNotificationSettings;
  notification_channels: string[];
  updated_at: number;
}

export interface ProductAlertResultItem extends ProductAlertListResultItem {
  notification_settings: ProductAlertNotificationSettings;
}

export interface ProductAlertsResult {
  hits: number;
  results: ProductAlertListResultItem[];
}

/** A workspace alert: one record per alert type, targeting the app group itself. */
export interface WorkspaceProductAlert extends ProductAlertListResultItem {
  alert_type: WorkspaceAlertType;
}

export interface PatchProductAlertProps {
  id: string;
  status: ProductAlertStatus;
}

export interface DeleteProductAlertProps {
  id: string;
}

/** Meta the backend attaches to the target-limit error: the cap it enforced. */
export interface ProductAlertTargetLimitReachedMeta {
  max_alerts: number;
}

export type ProductAlertTargetLimitReachedError = FetchUserEncodedError<
  SHARED_CONSTANTS_PRODUCT_ALERTS["ERROR_CODES"]["TARGET_ALERT_LIMIT_REACHED"],
  ProductAlertTargetLimitReachedMeta
>;

/**
 * Whether a failed product alert write was rejected because the target already carries its
 * maximum number of alerts (for example another user created one after this page loaded).
 */
export const isProductAlertTargetLimitReachedError = (
  error: unknown
): error is ProductAlertTargetLimitReachedError => {
  return error instanceof FetchUserEncodedError && error.errorCode === productAlertTargetLimitReachedErrorCode;
};
